import { ReadingLevelFilters } from "@tlc/ls2pac/components/BooleanSearchForm";
import { isPlainObject } from "../../../../util/isPlainObject";
import {
  isNonEmptySearchToken,
  isSearchToken,
  SearchToken,
} from "./SearchToken";

export interface AdvancedSearchTerm {
  isAnd: boolean;
  isReadingLevel: false;
  searchTokens: SearchToken[];
}

export interface ReadingLevelSearchTerm {
  isAnd: true;
  isReadingLevel: true;
  searchTokens: SearchToken[];
  readingLevelFilters: ReadingLevelFilters;
}

const isNullableNumber = (v: unknown): v is number | null =>
  v === null || (typeof v === "number" && Number.isFinite(v));

const isNullableString = (v: unknown): v is string | null =>
  v === null || typeof v === "string";

export function isReadingLevelFilters(x: unknown): x is ReadingLevelFilters {
  if (!isPlainObject(x)) return false;
  const o = x as Record<string, unknown>;
  return (
    "bookLevelStart" in o &&
    "bookLevelEnd" in o &&
    "interestLevel" in o &&
    "lexileStart" in o &&
    "lexileEnd" in o &&
    isNullableNumber(o.bookLevelStart) &&
    isNullableNumber(o.bookLevelEnd) &&
    isNullableString(o.interestLevel) &&
    isNullableNumber(o.lexileStart) &&
    isNullableNumber(o.lexileEnd)
  );
}

export function isNonEmptyReadingLevelFilters(
  x: unknown
): x is ReadingLevelFilters {
  if (!isReadingLevelFilters(x)) return false;
  return [
    x.bookLevelStart,
    x.bookLevelEnd,
    x.lexileStart,
    x.lexileEnd,
    x.interestLevel,
  ].some((v) => v !== null);
}

export function isAdvancedSearchTerm(x: unknown): x is AdvancedSearchTerm {
  if (!isPlainObject(x)) return false;
  const { isAnd, isReadingLevel, searchTokens } = x;

  if (typeof isAnd !== "boolean") return false;
  if (typeof isReadingLevel !== "boolean" || isReadingLevel === true)
    return false;
  if (!Array.isArray(searchTokens)) return false;
  if (!searchTokens.every(isNonEmptySearchToken)) return false;
  if (searchTokens.length < 1) return false;

  return true;
}

// TODO I moved the readingLevelFilters inside the term object of the URL
// this makes it possible to use a single getTerm, setTerm API for adapters of different searches
// this causes some complexity in Saved Searches—we have to check if the rawTerm is a ReadingLevelSearchTerm and
// then map readingLevelFilters to a different target property
// Same thing in LSSearch, we need a special getReadingLevelFiltersKey in order for changes to reading level inputs
// to trigger a state change and fire off a search
// We also need to check the rawTerm with isReadingLevelSearch in searchWithParameters
// Including this complexity inside reduces the effectiveness of the adapter pattern.
// It may make sense to have adapter functions that return...
// SearchParametersDwr and SaveSearchParams so the specificity remains in the adapter
export function isReadingLevelSearch(x: unknown): x is ReadingLevelSearchTerm {
  if (!isPlainObject(x)) return false;
  const { isAnd, isReadingLevel, searchTokens, readingLevelFilters } = x;
  if (typeof isAnd !== "boolean" || isAnd === false) return false;
  if (typeof isReadingLevel !== "boolean" || isReadingLevel === false)
    return false;
  if (!Array.isArray(searchTokens)) return false;
  if (!searchTokens.every(isSearchToken)) return false;
  if (searchTokens.length !== 1) return false;
  return isReadingLevelFilters(readingLevelFilters);
}
